import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { api, OfflineError } from '../lib/api'
import { useAuth } from '../hooks/useAuth'

/**
 * GuidanceStrip - the single soft guidance surface above the subject
 * grid on /subjects (UI_UX_IMPLEMENTATION_PLAN_MAY_2026.md Slice 1).
 *
 * Replaces the dominant DailyMissionCard hero. The plan principle is
 * "launcher, not controller": the strip suggests one next step in a
 * single line; the subject grid below remains the page's main
 * destination. No big illustrations, no large CTAs that compete with
 * the grid for first-paint attention.
 *
 * Priority-locked behaviour matrix (2026-05-12, top-down - first true
 * row wins):
 *
 *   1. Active mission (not completed)  → mission progress + Continuar
 *   2. Mission completed today         → quiet ✓ + Continuar a praticar
 *   3. Recent activity (any subject    → "Continuar onde paraste"
 *      has mastery_score !== null)         → deep-link to active_subject
 *   4. Fresh student (no history)      → soft starter recommendation
 *
 * Offline: hides silently (mission fetch fails on OfflineError; the
 * recent/fresh fallback uses local subjects prop, so still works).
 *
 * Acceptance: fits ~80px height on 360px width. One horizontal row.
 * Single primary CTA per state.
 */

type Mode = 'admission' | 'national_12'
export type GuidanceStripState =
  | 'loading'
  | 'active_mission'
  | 'mission_done'
  | 'recent_activity'
  | 'fresh_start'

interface DailyMission {
  mission_type:        string
  target_value:        number
  current_value:       number
  completed_at:        string | null
  pinned_subject_code: string | null
  pinned_topic_id:     string | null
  pinned_topic_name?:  string | null
  copy?: { title?: string | null; body?: string | null } | null
}

interface SubjectLite {
  code:          string
  name:          string
  mastery_score: number | null
  subject_weight?: number
}

interface Props {
  mode:          Mode
  activeSubject: string
  subjects:      SubjectLite[]
  /** Routes to the underlying mode + university filter so deep-links
   *  carry the same context the rest of /subjects uses. Pass
   *  `?mode=…&university=…` (with the leading "?" included) or empty
   *  string. */
  navSuffix:     string
  onStateChange?: (state: GuidanceStripState) => void
}

/** Build /questions deep-link for a mission. */
function missionCtaTarget(m: DailyMission, mode: Mode): string | null {
  if (m.mission_type === 'complete_one_exam_simulation') return `/exam?mode=${mode}`
  if (m.pinned_topic_id) {
    const tn  = m.pinned_topic_name ? `&topicName=${encodeURIComponent(m.pinned_topic_name)}` : ''
    const sub = m.pinned_subject_code ? `subject=${m.pinned_subject_code}&` : ''
    return `/questions?${sub}topic=${m.pinned_topic_id}${tn}&mode=${mode}`
  }
  if (m.pinned_subject_code) {
    return `/questions?subject=${m.pinned_subject_code}&mode=${mode}`
  }
  return null
}

export function GuidanceStrip({ mode, activeSubject, subjects, navSuffix, onStateChange }: Props) {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [mission, setMission] = useState<DailyMission | null>(null)
  const [missionLoaded, setMissionLoaded] = useState(false)
  const [hideCompletedMission, setHideCompletedMission] = useState(false)
  const hasActivity = subjects.some(s => s.mastery_score !== null)

  useEffect(() => {
    // Phase 4 (2026-06-12): defer the /daily-mission fetch behind
    // requestIdleCallback so the strip — which is non-critical chrome
    // above the subject grid — doesn't compete with /subjects for the
    // network slot on 3G/2G. The strip renders a `state='loading'`
    // placeholder until the fetch resolves; pre-fetch fires fast enough
    // that students rarely see it.
    let cancelled = false
    setMissionLoaded(false)
    setHideCompletedMission(false)
    const fire = () => {
      if (cancelled) return
      // GET /daily-mission is enforceNonAnonymous. The strip falls through to
      // its recent/fresh branches without a mission, which is what it already
      // does when the fetch fails, so a guest loses nothing but the 403.
      if (user?.is_anonymous) { setMissionLoaded(true); return }
      api.get<DailyMission>(`/daily-mission?mode=${mode}`)
        .then(d => { if (!cancelled) { setMission(d); setMissionLoaded(true) } })
        .catch((err: unknown) => {
          if (cancelled) return
          // Offline OR transient - fall through to the recent/fresh
          // branches below using the subjects prop already in memory.
          if (!(err instanceof OfflineError)) {
            /* swallowed: best-effort fetch */
          }
          setMission(null)
          setMissionLoaded(true)
        })
    }
    if ('requestIdleCallback' in window) {
      const id = (window as Window & typeof globalThis).requestIdleCallback(fire, { timeout: 2000 })
      return () => {
        cancelled = true
        ;(window as Window & typeof globalThis).cancelIdleCallback(id)
      }
    }
    const t = setTimeout(fire, 200)
    return () => { cancelled = true; clearTimeout(t) }
  }, [mode])

  // Quietly dismiss the "mission completed" celebration after 10s so
  // it can acknowledge the win without occupying the top suggestion
  // slot for too long.
  useEffect(() => {
    if (!mission?.completed_at) return
    const t = window.setTimeout(() => setHideCompletedMission(true), 10_000)
    return () => window.clearTimeout(t)
  }, [mission?.completed_at])

  useEffect(() => {
    if (!onStateChange) return
    if (!missionLoaded) {
      onStateChange('loading')
      return
    }
    if (mission && !mission.completed_at) {
      onStateChange('active_mission')
      return
    }
    if (mission && mission.completed_at && !hideCompletedMission) {
      onStateChange('mission_done')
      return
    }
    if (hasActivity) {
      onStateChange('recent_activity')
      return
    }
    onStateChange('fresh_start')
  }, [onStateChange, missionLoaded, mission, hideCompletedMission, hasActivity])

  // Wait for the mission probe to resolve before deciding which case
  // to render. Avoids a flash of "Continuar onde paraste" that then
  // gets replaced by a mission card a tick later.
  if (!missionLoaded) {
    return (
      <div className="rounded-xl border border-indigo-100 bg-white px-4 py-3 mb-4 animate-pulse">
        <div className="h-3 bg-indigo-50 rounded w-2/3 mb-2" />
        <div className="h-3 bg-indigo-50 rounded w-1/3" />
      </div>
    )
  }

  // ── Case 1: active mission (not completed) ──────────────────────
  if (mission && !mission.completed_at) {
    const pct = mission.target_value > 0
      ? Math.min(100, Math.round((mission.current_value / mission.target_value) * 100))
      : 0
    const target = missionCtaTarget(mission, mode)
    const missionTitle = mission.copy?.title?.trim()
      || mission.copy?.body?.trim()
      || 'Continua a tua missão de hoje'
    return (
      <div className="rounded-xl border border-indigo-100 bg-indigo-50/50 px-4 py-3 mb-4">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-indigo-600">
              Missão de hoje
            </p>
            <p className="text-sm text-gray-800 truncate">
              {missionTitle}
            </p>
            <div className="flex items-center gap-2 mt-1.5">
              <div className="h-1 flex-1 bg-indigo-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-indigo-500 transition-all"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="text-[11px] text-indigo-700 tabular-nums">
                {mission.current_value}/{mission.target_value}
              </span>
            </div>
          </div>
          <button
            onClick={() => target ? navigate(target) : window.scrollBy({ top: 200, behavior: 'smooth' })}
            className="shrink-0 bg-indigo-600 text-white text-sm font-semibold px-3 py-2 rounded-lg hover:bg-indigo-700"
          >
            Continuar
          </button>
        </div>
      </div>
    )
  }

  // ── Case 2: mission completed today ─────────────────────────────
  if (mission && mission.completed_at && !hideCompletedMission) {
    return (
      <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 mb-4">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-green-800">
              Missão de hoje feita ✓
            </p>
          </div>
          <button
            onClick={() => window.scrollBy({ top: 200, behavior: 'smooth' })}
            className="shrink-0 text-sm font-semibold text-green-700 hover:text-green-900"
          >
            Continuar a praticar
          </button>
        </div>
      </div>
    )
  }

  // ── Cases 3 & 4: no active mission. Detect activity from subjects. ──
  // Case 3: recent activity → "Continuar onde paraste".
  if (hasActivity) {
    const lastSubject = subjects.find(s => s.code === activeSubject) ?? subjects[0]
    return (
      <div className="rounded-xl border border-gray-100 bg-white px-4 py-3 mb-4">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              Continuar onde paraste
            </p>
            <p className="text-sm text-gray-800 truncate">
              {lastSubject?.name ?? 'Praticar'}
            </p>
          </div>
          <button
            onClick={() => navigate(`/questions?subject=${lastSubject?.code ?? 'MAT'}${navSuffix.startsWith('?') ? '&' + navSuffix.slice(1) : navSuffix}`)}
            className="shrink-0 bg-indigo-600 text-white text-sm font-semibold px-3 py-2 rounded-lg hover:bg-indigo-700"
          >
            Praticar
          </button>
        </div>
      </div>
    )
  }

  // Case 4: fresh student. Soft recommendation - heaviest required
  // subject if programmes are chosen, else the first subject in the
  // grid. Never a wall; the subject grid below stays available.
  const recommendation =
    [...subjects].sort((a, b) => (b.subject_weight ?? 0) - (a.subject_weight ?? 0))[0]
    ?? subjects[0]
  return (
    <div className="rounded-xl border border-gray-100 bg-white px-4 py-3 mb-4">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            Para começar
          </p>
          <p className="text-sm text-gray-800 truncate">
            Escolhe uma disciplina abaixo{recommendation ? `, ${recommendation.name} é um bom início` : ''}.
          </p>
        </div>
        {recommendation && (
          <button
            onClick={() => navigate(`/questions?subject=${recommendation.code}${navSuffix.startsWith('?') ? '&' + navSuffix.slice(1) : navSuffix}`)}
            className="shrink-0 bg-indigo-600 text-white text-sm font-semibold px-3 py-2 rounded-lg hover:bg-indigo-700"
          >
            Começar
          </button>
        )}
      </div>
    </div>
  )
}
